const DISMISSED_PRELOADED_REVIEW_USERS_KEY = 'support.dismissedPreloadedReviewUsers'
const DISMISSED_PRELOADED_REVIEW_USERS_TTL = 6 * 60 * 60 * 1000

const parseDismissedPreloadedReviewUsers = () => {
  try {
    const storedValue = localStorage.getItem(DISMISSED_PRELOADED_REVIEW_USERS_KEY)
    const parsedValue = JSON.parse(storedValue || '[]')

    return Array.isArray(parsedValue) ? parsedValue : []
  } catch (error) {
    return []
  }
}

const saveDismissedPreloadedReviewUsers = (entries) => {
  localStorage.setItem(
    DISMISSED_PRELOADED_REVIEW_USERS_KEY,
    JSON.stringify(entries)
  )
}

const getDismissedPreloadedReviewUsers = () => {
  const now = Date.now()
  const activeEntries = parseDismissedPreloadedReviewUsers().filter(
    ({ userId, expiresAt }) => Number.isFinite(Number(userId)) && Number(expiresAt) > now
  )

  saveDismissedPreloadedReviewUsers(activeEntries)

  return activeEntries
}

const getDismissedPreloadedReviewUserIds = () => new Set(
  getDismissedPreloadedReviewUsers().map(({ userId }) => Number(userId))
)

const dismissPreloadedReviewUser = (userId) => {
  const normalizedUserId = Number(userId)
  const entries = getDismissedPreloadedReviewUsers().filter(
    (entry) => Number(entry.userId) !== normalizedUserId
  )

  entries.push({
    userId: normalizedUserId,
    expiresAt: Date.now() + DISMISSED_PRELOADED_REVIEW_USERS_TTL
  })

  saveDismissedPreloadedReviewUsers(entries)
}

const restoreDismissedPreloadedReviewUser = (userId) => {
  const normalizedUserId = Number(userId)
  const entries = getDismissedPreloadedReviewUsers().filter(
    (entry) => Number(entry.userId) !== normalizedUserId
  )

  saveDismissedPreloadedReviewUsers(entries)
}

const STALE_REASSIGN_REASONS = [
  'Already reassigned or original review not found',
  'Open review already exists for this user'
]

const getAvailableReviewsForAdmin = (state, adminId) => {
  const dismissedUserIds = getDismissedPreloadedReviewUserIds()

  return state.reviews.filter(
    (review) => review.admin &&
      review.admin.id === adminId &&
      !dismissedUserIds.has(review.user.id)
  )
}

const getFirstVisibleReview = (reviews) => {
  const dismissedUserIds = getDismissedPreloadedReviewUserIds()

  return (
    reviews.find((review) => !dismissedUserIds.has(review.user.id)) || null
  )
}

export default {
  namespaced: 'support',
  state: {
    timerInterval: null,
    dateNow: new Date(),
    reviews: [],
    reviewsHistory: [],
    reviewsLoading: false,
    reviewsPage: 1,
    reviewsPerPage: 30,
    reviewsLastPage: 2,
    reviewsSort: 'desc',
    currentReview: null,
    selectedReviews: [],
    resetSelectedReviews: false,
    preloadedUsers: [],
    preloadCount: 1,
    isRemovedByWebsocket: false,
    shouldActivateTabIndex: null,
    filterOptions: {
      user_id: null,
      admin_ids: [],
      apps: [],
      user_statuses: [],
      countries: [],
      status: 1
    },
    userIdFilterOption: null,
    adminIdFilterOptions: [],
    appFilterOptions: [],
    userStatusFilterOptions: [],
    reviewStatusFilterOptions: [],
    adminStatus: 0,
    assignees: [],
    statistic: null,
    notificationSound: 'notification',
    storageUrl: process.env.VUE_APP_CDN_DOMAIN,
    currentlyPlayingAudio: null,
    showMoreReviews: false
  },
  getters: {
    hasFilterOptions (state) {
      return (
        state.filterOptions.user_id ||
        state.filterOptions.admin_ids.length ||
        state.filterOptions.apps.length ||
        state.filterOptions.user_statuses.length ||
        state.filterOptions.countries.length ||
        state.filterOptions.status
      )
    }
  },
  mutations: {
    ADD_REVIEWS (state, value) {
      state.reviews.push(...value)
    },
    SET_REVIEWS_HISTORY (state, value) {
      state.reviewsHistory = value
    },
    SET_REVIEWS_LOADING (state, value) {
      state.reviewsLoading = value
    },
    REMOVE_REVIEWS (state) {
      state.reviews = []
    },
    SET_REVIEWS_PAGE (state, value) {
      state.reviewsPage = value
    },
    SET_REVIEWS_LAST_PAGE (state, value) {
      state.reviewsLastPage = value
    },
    SET_REVIEWS_SORT (state, value) {
      state.reviewsSort = value
    },
    SET_CURRENT_REVIEW (state, value) {
      state.currentReview = value
    },
    SET_SELECTED_REVIEWS (state, value) {
      state.selectedReviews = value
    },
    async RESET_SELECTED_REVIEWS (state) {
      state.selectedReviews = []
      state.resetSelectedReviews = true

      await this._vm.$nextTick()

      state.resetSelectedReviews = false
    },
    ADD_PRELOADED_USERS (state, value) {
      const dismissedUserIds = getDismissedPreloadedReviewUserIds()
      const ids = new Set()
      const arr = [
        ...state.preloadedUsers,
        ...value.filter((review) => !dismissedUserIds.has(review.user.id))
      ]
      state.preloadedUsers = arr.filter(
        ({ uuid }) => !ids.has(uuid) && ids.add(uuid)
      )
      // .sort(function (x, y) {
      //   return x.uuid === first ? -1 : y.uuid === first ? 1 : 0
      // })
    },
    REMOVE_PRELOADED_USER (state, value) {
      const exists = state.preloadedUsers.some((item) => item.uuid === value)
      if (exists) {
        state.preloadedUsers = state.preloadedUsers.filter(
          (item) => item.uuid !== value
        )
      }
    },
    DISMISS_PRELOADED_USER (state, review) {
      dismissPreloadedReviewUser(review.user.id)
      state.preloadedUsers = state.preloadedUsers.filter(
        (item) => item.uuid !== review.uuid
      )

      if (state.currentReview && state.currentReview.uuid === review.uuid) {
        state.currentReview = null
      }
    },
    CLEANUP_DISMISSED_PRELOADED_USERS () {
      getDismissedPreloadedReviewUsers()
    },
    RESET_PRELOADED_USERS (state) {
      state.preloadedUsers = []
    },
    MAINTAIN_PRELOADED_USERS (state) {
      const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
      const currentAdminId = adminInfo.id

      const hasAdminFilter = state.filterOptions.admin_ids.includes(currentAdminId)
      const availableReviewsForAdmin = getAvailableReviewsForAdmin(state, currentAdminId)
      const dismissedUserIds = getDismissedPreloadedReviewUserIds()
      let targetCount = 10
      if (availableReviewsForAdmin.length < targetCount) {
        targetCount = availableReviewsForAdmin.length
      }
      const currentCount = state.preloadedUsers.length
      const needToAdd = targetCount - currentCount
      if (hasAdminFilter && needToAdd > 0) {
      
        state.preloadedUsers = state.preloadedUsers.map(preloadedUser => {
          const updatedReview = state.reviews.find(review => review.uuid === preloadedUser.uuid)
          return updatedReview || preloadedUser
        })
      
        state.preloadedUsers = state.preloadedUsers.filter(
          (user) => user.admin &&
            user.admin.id === currentAdminId &&
            !dismissedUserIds.has(user.user.id)
        )
      
      
        if (needToAdd > 0) {
          const existingUserIds = new Set(state.preloadedUsers.map(user => user.user.id))
        
          const availableReviews = availableReviewsForAdmin.filter(
            (review) => !existingUserIds.has(review.user.id)
          )
        
          const reviewsToAdd = availableReviews.slice(0, needToAdd)
          state.preloadedUsers.push(...reviewsToAdd)
        }
      }
    },
    LOAD_SINGLE_USER_TO_TABS (state, { review, shouldActivate = false }) {
      const MAX_TABS = 10
      restoreDismissedPreloadedReviewUser(review.user.id)
      
      const existingIndex = state.preloadedUsers.findIndex(user => user.user.id === review.user.id)
      
      if (existingIndex !== -1) {
        if (shouldActivate) {
          state.shouldActivateTabIndex = existingIndex
        }
      } else {
        state.preloadedUsers.unshift(review)
        
        if (state.preloadedUsers.length > MAX_TABS) {
          state.preloadedUsers = state.preloadedUsers.slice(0, MAX_TABS)
        }
        
        if (shouldActivate) {
          state.shouldActivateTabIndex = 0
        }
      }
    },
    AUTO_LOAD_ADMIN_USERS (state) {
      const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
      const currentAdminId = adminInfo.id
      const TARGET_COUNT = 10
      
      const hasAdminFilter = state.filterOptions.admin_ids.includes(currentAdminId)
      
      if (hasAdminFilter) {
        const availableReviews = getAvailableReviewsForAdmin(state, currentAdminId)

        state.preloadedUsers = availableReviews.slice(0, TARGET_COUNT)
      }
    },
    MAINTAIN_PRELOADED_USERS_IF_ADMIN_FILTERED (state) {
      const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
      const currentAdminId = adminInfo.id
      
      const hasAdminFilter = state.filterOptions.admin_ids.includes(currentAdminId)
      const availableReviewsForAdmin = getAvailableReviewsForAdmin(state, currentAdminId)
      const dismissedUserIds = getDismissedPreloadedReviewUserIds()
      let targetCount = 10
      if (availableReviewsForAdmin.length < targetCount) {
        targetCount = availableReviewsForAdmin.length
      }
      const currentCount = state.preloadedUsers.length
      const needToAdd = targetCount - currentCount
      if (hasAdminFilter && needToAdd > 0) {

        
        state.preloadedUsers = state.preloadedUsers.map(preloadedUser => {
          const updatedReview = state.reviews.find(review => review.uuid === preloadedUser.uuid)
          return updatedReview || preloadedUser
        })
        
        state.preloadedUsers = state.preloadedUsers.filter(
          (user) => user.admin &&
            user.admin.id === currentAdminId &&
            !dismissedUserIds.has(user.user.id)
        )
        
        const existingUserIds = new Set(state.preloadedUsers.map(user => user.user.id))
          
        const availableReviews = availableReviewsForAdmin.filter(
          (review) => !existingUserIds.has(review.user.id)
        )
          
        const reviewsToAdd = availableReviews.slice(0, needToAdd)
        state.preloadedUsers.push(...reviewsToAdd)
      }
    },
    SET_PRELOAD_COUNT (state, value) {
      state.preloadCount = value
    },
    ADD_PRELOADED_USERS_COUNT (state, limit) {
      const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
      const currentAdminId = adminInfo.id
      const dismissedUserIds = getDismissedPreloadedReviewUserIds()
      
      const limitAll = limit + state.preloadedUsers.length
      const seenIds = new Set(state.preloadedUsers.map((obj) => obj.user.id))
      const uniqueObjects = [...state.preloadedUsers]
      let index = 0
      if (state.preloadedUsers.length) {
        index = state.reviews.findIndex(
          (item) => item.user.id === state.preloadedUsers[state.preloadedUsers.length - 1].user.id
        )
      }

      const slicedArray = state.reviews.slice(index).filter(
        (obj) => obj.admin &&
          obj.admin.id === currentAdminId &&
          !dismissedUserIds.has(obj.user.id)
      )

      for (const obj of slicedArray) {
        if (!seenIds.has(obj.user.id)) {
          seenIds.add(obj.user.id)
          uniqueObjects.push(obj)
          if (uniqueObjects.length === limitAll) {
            break
          }
        }
      }

      state.preloadedUsers = uniqueObjects
    },
    SET_FILTER_OPTIONS (state, value) {
      state.filterOptions = { ...state.filterOptions, ...value }
    },
    SET_FILTER_ADMIN_OPTIONS (state, value) {
      state.filterOptions.admin_ids = [value]
      const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
      if (value === adminInfo.id && state.reviews.length > 0) {
        const TARGET_COUNT = 10
        const availableReviews = getAvailableReviewsForAdmin(state, value)
        state.preloadedUsers = availableReviews.slice(0, TARGET_COUNT)
      }
    },
    SET_USER_ID_OPTION (state, value) {
      state.userIdFilterOption = value
    },
    SET_ADMIN_ID_OPTIONS (state, value) {
      state.adminIdFilterOptions = value
    },
    SET_APP_OPTIONS (state, value) {
      state.appFilterOptions = value
    },
    SET_USER_STATUS_OPTIONS (state, value) {
      state.userStatusFilterOptions = value
    },
    SET_REVIEW_STATUS_OPTIONS (state, value) {
      state.reviewStatusFilterOptions = value
    },
    REMOVE_FILTER_OPTIONS (state) {
      state.userIdFilterOption = null
      state.adminIdFilterOptions = []
      state.appFilterOptions = []
      state.userStatusFilterOptions = []
      state.filterOptions.countries = []
      // Don't reset status here - preserve URL-parsed or user-selected value
    },
    SET_ADMIN_STATUS (state, value) {
      state.adminStatus = value
    },
    SET_ASSIGNEES (state, value) {
      state.assignees = value
    },
    SET_STATISTIC (state, value) {
      state.statistic = value
    },
    SET_REMOVED_BY_WEBSOCKET (state, value) {
      state.isRemovedByWebsocket = value
    },
    CLEAR_SHOULD_ACTIVATE_FLAG (state) {
      state.shouldActivateTabIndex = null
    },
    SET_SHOW_MORE_REVIEWS (state, value) {
      state.showMoreReviews = value
    }
  },
  actions: {
    // eslint-disable-next-line no-empty-pattern
    showErrorMessage ({}, { errors = null }) {
      if (errors && errors.response && errors.response.data) {
        this._vm.alertError(
          errors.response.data.errors || errors.response.data.message
        )
      } else {
        this._vm.alertError('Something went wrong')
      }
    },

    runTimer ({ state }) {
      state.timerInterval = setInterval(() => {
        state.dateNow = new Date()
      }, 1000)
    },

    resetTimer ({ state }) {
      clearInterval(state.timerInterval)
      state.timerInterval = null
    },

    resetReviews ({ commit }) {
      commit('REMOVE_REVIEWS')
      commit('SET_CURRENT_REVIEW', null)
    },

    resetPages ({ commit }) {
      commit('SET_REVIEWS_PAGE', 1)
      commit('SET_REVIEWS_LAST_PAGE', 2)
    },

    isNotMatchUserId ({ state }, { id }) {
      return (
        state.filterOptions?.user_id && state.filterOptions?.user_id !== id
      )
    },

    isNotMatchAdminId ({ state }, { id }) {
      return (
        state.filterOptions?.admin_ids.length &&
        state.filterOptions?.admin_ids.indexOf(id) === -1
      )
    },

    isNotMatchApp ({ state }, { id }) {
      return (
        state.filterOptions?.apps.length &&
        state.filterOptions?.apps.indexOf(id) === -1
      )
    },

    isNotMatchUserStatus ({ state }, { status }) {
      return (
        state.filterOptions?.user_statuses.length &&
        state.filterOptions?.user_statuses.indexOf(status) === -1
      )
    },

    isNotMatchReviewStatus ({ state }, { status }) {
      return (
        state.filterOptions?.status && 
        state.filterOptions.status !== status
      )
    },

    async getReviews ({ state, commit, dispatch }, { queryReviewId = null }) {
      if (state.reviewsPage === 1) {
        dispatch('resetReviews')
      }

      if (state.reviewsLastPage >= state.reviewsPage) {
        commit('SET_REVIEWS_LOADING', true)

        return this._vm.$httpSupport
          .post(
            `/api/user-reviews?page=${state.reviewsPage}&per_page=${state.reviewsPerPage}`,
            {
              filters: { ...state.filterOptions },
              sort: state.reviewsSort,
              show_more_reviews: state.showMoreReviews
            }
          )
          .then((response) => {
            if (!this._vm.handleResponseError(response)) {
              const { data, pagination } = response.data
              commit('ADD_REVIEWS', data)

              if (!state.reviews.length) return

              if (state.reviewsPage === 1) {
                if (queryReviewId) {
                  const review = state.reviews.find(
                    (item) => item.uuid === queryReviewId
                  )
                  if (review) {
                    commit('SET_CURRENT_REVIEW', review)
                    commit('ADD_PRELOADED_USERS', [review])
                  }
                }

                if (!state.currentReview) {
                  // Open first review on init load reviews
                  dispatch('openFirstReview')
                } else {
                  commit('AUTO_LOAD_ADMIN_USERS')
                }
              }

              commit('SET_REVIEWS_PAGE', pagination.current_page + 1)
              commit('SET_REVIEWS_LAST_PAGE', pagination.last_page)
            }
          })
          .catch((errors) => {
            dispatch('showErrorMessage', { errors })
          })
          .finally(() => {
            commit('SET_REVIEWS_LOADING', false)
          })
      }
    },

    async getReviewsHistory ({ commit, dispatch }, { userId }) {
      return this._vm.$httpSupport
        .post('/api/user-reviews?page=1&per_page=99', {
          filters: { user_id: userId },
          sort: 'desc'
        })
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            const { data } = response.data
            commit('SET_REVIEWS_HISTORY', data)
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
    },

    async getPossibleFilterOptions ({ commit, dispatch }) {
      commit('REMOVE_FILTER_OPTIONS')

      return this._vm.$httpSupport
        .get('/api/user-reviews/list/filters')
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            const { user_id, admin_id, apps, user_status, status } = response.data
            commit('SET_USER_ID_OPTION', user_id)
            commit('SET_ADMIN_ID_OPTIONS', admin_id)
            commit('SET_APP_OPTIONS', apps)
            commit('SET_USER_STATUS_OPTIONS', user_status)
            commit('SET_REVIEW_STATUS_OPTIONS', status)
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
    },

    async openFirstReview ({ state, commit }) {
      commit('SET_CURRENT_REVIEW', null)
      // commit('RESET_PRELOADED_USERS')

      await this._vm.$nextTick()

      // Open first review
      const firstVisibleReview = getFirstVisibleReview(state.reviews)
      if (firstVisibleReview) {
        commit('SET_CURRENT_REVIEW', firstVisibleReview)
        commit('ADD_PRELOADED_USERS', [firstVisibleReview])
      }
      
      commit('MAINTAIN_PRELOADED_USERS_IF_ADMIN_FILTERED')
    },

    async updateReviewStatus ({ state, commit, dispatch }, { uuid, status }) {
      const targetReview = state.reviews.find((review) => review.uuid === uuid)

      if (targetReview) {
        targetReview.user.status = status
      }

      const isNotMatched = await dispatch('isNotMatchUserStatus', {
        status: targetReview.user.status
      })

      if (isNotMatched) {
        state.reviews = state.reviews.filter((review) => review.uuid !== uuid)
      }
      
      commit('MAINTAIN_PRELOADED_USERS_IF_ADMIN_FILTERED')
    },

    async addReviewFromSocket ({ state, commit, dispatch }, { data, id }) {
      const isNotMatchUserId = await dispatch('isNotMatchUserId', {
        id: data.user.id
      })
      const isNotMatchAdminId = await dispatch('isNotMatchAdminId', {
        id: data.admin.id
      })
      const isNotMatchApp = await dispatch('isNotMatchApp', {
        id: data.user.app.id
      })
      const isNotMatchUserStatus = await dispatch('isNotMatchUserStatus', {
        status: data.user.status
      })
      const isNotMatchReviewStatus = await dispatch('isNotMatchReviewStatus', {
        status: data.status
      })

      if (
        isNotMatchUserId ||
        isNotMatchAdminId ||
        isNotMatchApp ||
        isNotMatchUserStatus ||
        isNotMatchReviewStatus
      ) {
        return
      }

      // Only add to list if the review is assigned to the current admin
      if (data.assignee && data.admin.id !== id) {
        return
      }

      // Check if review already exists in the list
      const existingReview = state.reviews.find(
        (review) => review.uuid === data.uuid
      )

      if (existingReview) {
        Object.assign(existingReview, data)
        
        if (!state.currentReview) {
          dispatch('openFirstReview')
        }

        if (data.admin.id === id) {
          dispatch('playSound', { source: state.notificationSound })
        }
        // eslint-disable-next-line brace-style
      }
      // If review doesn't exist, add it to the beginning of the reviews array
      else {
        if (state.reviewsSort === 'desc') {
          state.reviews.unshift(data)
        } else {
          state.reviews.push(data)
        }
        
        if (state.reviews.length > state.reviewsPerPage) {
          if (state.reviewsSort === 'desc') {
            state.reviews = state.reviews.slice(0, state.reviewsPerPage)
          } else {
            state.reviews = state.reviews.slice(-state.reviewsPerPage)
          }
        }

        // If no current review is selected, open the first one
        if (!state.currentReview) {
          dispatch('openFirstReview')
        }

        // Play notification sound for assigned reviews
        if (data.admin.id === id) {
          dispatch('playSound', { source: state.notificationSound })
        }
      }

      if (data.admin.id === id) {
        const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
        const currentAdminId = adminInfo.id
        
        if (!state.filterOptions.admin_ids.includes(currentAdminId)) {
          commit('SET_FILTER_OPTIONS', {
            ...state.filterOptions,
            admin_ids: [...state.filterOptions.admin_ids, currentAdminId]
          })
        }
      }
      
      commit('MAINTAIN_PRELOADED_USERS_IF_ADMIN_FILTERED')
    },

    removeReviewFromSocket ({ state, commit }, { data }) {
      // Set flag to indicate removal by websocket
      commit('SET_REMOVED_BY_WEBSOCKET', true)
      
      // Remove review from the list by UUID
      const reviewIndex = state.reviews.findIndex(
        (review) => review.uuid === data.uuid
      )

      if (reviewIndex !== -1) {
        state.reviews.splice(reviewIndex, 1)

        if (state.currentReview && state.currentReview.uuid === data.uuid) {
          commit('SET_CURRENT_REVIEW', null)
        }
      }
      
      // Remove from preloadedUsers if present
      commit('REMOVE_PRELOADED_USER', data.uuid)
      
      commit('MAINTAIN_PRELOADED_USERS_IF_ADMIN_FILTERED')
    },

    removeUnavailableReview ({ state, commit }, { uuid, activateNext = true }) {
      const shouldAdvanceTab = activateNext && (
        state.preloadedUsers.some((item) => item.uuid === uuid) ||
        state.currentReview?.uuid === uuid
      )

      if (shouldAdvanceTab) {
        commit('SET_REMOVED_BY_WEBSOCKET', true)
      }

      const reviewIndex = state.reviews.findIndex(
        (review) => review.uuid === uuid
      )

      if (reviewIndex !== -1) {
        state.reviews.splice(reviewIndex, 1)
      }

      if (state.currentReview && state.currentReview.uuid === uuid) {
        commit('SET_CURRENT_REVIEW', null)
      }

      commit('REMOVE_PRELOADED_USER', uuid)
      commit('MAINTAIN_PRELOADED_USERS_IF_ADMIN_FILTERED')
    },

    updateStatisticFromSocket ({ commit }, { data }) {
      // Update statistic data with data from WebSocket event
      commit('SET_STATISTIC', data)
    },

    async getAdminStatus ({ commit, dispatch }) {
      return await this._vm.$httpSupport
        .get('/api/admin')
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            const { data } = response.data
            const { support_review_status } = data

            commit('SET_ADMIN_STATUS', support_review_status)
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
    },

    async setAdminStatus ({ commit, dispatch, state }, { status }) {
      return await this._vm.$httpSupport
        .put('/api/admin', {
          support_review_status: status
        })
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            commit('SET_ADMIN_STATUS', status)
            
            if (status === 0) {
              
              const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
              const currentAdminId = adminInfo.id
              
              if (!state.filterOptions.admin_ids.includes(currentAdminId)) {
                const newFilterOptions = {
                  ...state.filterOptions,
                  admin_ids: [...state.filterOptions.admin_ids, currentAdminId]
                }
                commit('SET_FILTER_OPTIONS', newFilterOptions)
              }
            }
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
    },

    // Reassign user review
    async getAssignees ({ commit, dispatch }) {
      return await this._vm.$httpSupport
        .post('/api/admins/filter', {
          is_review_admin: 1,
          support_review_status: [0, 1]
        })
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            const { data } = response.data

            commit('SET_ASSIGNEES', data)
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
    },

    async setSupportAssignee ({ dispatch, commit }, { adminId, uuids }) {
      return await this._vm.$httpSupport
        .put('/api/user-reviews/reassign', {
          admin_id: adminId,
          user_review_uuids: uuids
        })
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            const { failed_uuids } = response.data
            const failedUuidSet = new Set(failed_uuids.map(({ uuid }) => uuid))
            const successfulOriginalUuids = uuids.filter((uuid) => !failedUuidSet.has(uuid))
            const staleFailures = failed_uuids.filter(({ reason }) => STALE_REASSIGN_REASONS.includes(reason))
            const otherFailures = failed_uuids.filter(({ reason }) => !STALE_REASSIGN_REASONS.includes(reason))

            if (successfulOriginalUuids.length) {
              this._vm.alertSuccess(
                `Successful UUIDs: ${successfulOriginalUuids.join(', ')}`
              )
            }

            successfulOriginalUuids.forEach((uuid) => {
              dispatch('removeUnavailableReview', { uuid })
            })

            if (staleFailures.length) {
              staleFailures.forEach(({ uuid }) => {
                dispatch('removeUnavailableReview', { uuid })
              })
              this._vm.alertWarning('This review is no longer available')
            }

            if (otherFailures.length) {
              this._vm.alertError(
                `Failed UUIDs: ${otherFailures
                  .map(({ uuid, reason }) => `${uuid}: ${reason}`)
                  .join(', ')}`
              )
            }

            commit('RESET_SELECTED_REVIEWS')
            
            commit('MAINTAIN_PRELOADED_USERS_IF_ADMIN_FILTERED')
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
    },

    // Statistic user review
    async getStatistic ({ commit, dispatch }) {
      return await this._vm.$httpSupport
        .get('/api/user-reviews/statistic')
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            const res = response.data

            commit('SET_STATISTIC', res)
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
    },

    async getNotes ({ commit, getters, dispatch }) {
      commit('SET_NOTES_LOADING', true)

      return this._vm.$httpChat
        .get(
          `/api/admin/apps/${getters.externalAppId}/users/${getters.externalUserId}/notes`
        )
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            commit('SET_NOTES', response.data)
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
        .finally(() => {
          commit('SET_NOTES_LOADING', false)
        })
    },

    async sendNote ({ commit, getters, dispatch }, { text }) {
      commit('SET_NOTE_LOADING', true)

      return this._vm.$httpChat
        .post(
          `/api/admin/apps/${getters.externalAppId}/users/${getters.externalUserId}/notes`,
          {
            text
          }
        )
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            commit('ADD_NOTE', response.data)
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
        .finally(() => {
          commit('SET_NOTE_LOADING', false)
        })
    },

    async deleteNote ({ commit, getters, dispatch }, { noteId }) {
      return this._vm.$httpChat
        .delete(
          `/api/admin/apps/${getters.externalAppId}/users/${getters.externalUserId}/notes/${noteId}`
        )
        .then((response) => {
          if (!this._vm.handleResponseError(response)) {
            const { status } = response

            if (status === 200) {
              commit('DELETE_NOTE', noteId)
            }
          }
        })
        .catch((errors) => {
          dispatch('showErrorMessage', { errors })
        })
    },

    clearSound ({ state }) {
      if (state.currentlyPlayingAudio) {
        state.currentlyPlayingAudio.pause()
        state.currentlyPlayingAudio = null
      }
    },

    playSound ({ state, dispatch }, { source }) {
      if (state.adminStatus === 0) {
        dispatch('clearSound')
        state.currentlyPlayingAudio = new Audio(
          `${state.storageUrl}/${source}.mp3`
        )
        state.currentlyPlayingAudio.volume = 0.3
        state.currentlyPlayingAudio.play()
      }
    }
  }
}
